import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { format } from "date-fns";
import { motion } from "framer-motion";
import {
  ArrowLeft, Calendar, MapPin, Users, CreditCard, Wallet,
  Loader2, Ticket, ExternalLink, Radio,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useWallet } from "@/hooks/useWallet";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";

const BASE = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

/* ── Types ── */
interface ISMEvent {
  id: number;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  spaceName: string;
  hostName: string;
  hostAvatar: string | null;
  imageUrl: string;
  attendeeCount: number;
  maxAttendees: number;
  category: string;
  ticketPrice: number;
  currency: string;
  live: boolean;
}

interface TicketPackage {
  name: string;
  detail: string;
  price: number | null;
  isFree: boolean;
  isFromPrice: boolean;
  label: string;
  tier: number; // 0=GA, 1=VIP, 2=Sponsor, 3=Artist/Other
}

/* ── Tier styling ── */
const TIER_STYLES = [
  { badge: "#22c55e", bg: "rgba(34,197,94,.08)",  border: "rgba(34,197,94,.25)",  label: "GENERAL" },
  { badge: "#4fe4d0", bg: "rgba(79,228,208,.08)",  border: "rgba(79,228,208,.3)",  label: "VIP"     },
  { badge: "#60a5fa", bg: "rgba(96,165,250,.08)",  border: "rgba(96,165,250,.25)", label: "SPONSOR" },
  { badge: "#a78bfa", bg: "rgba(167,139,250,.08)", border: "rgba(167,139,250,.25)",label: "ARTIST"  },
] as const;

/* ── Parse "TICKET PACKAGES: ..." from event description ── */
function parseTicketPackages(description: string): TicketPackage[] {
  const match = description.match(/TICKET PACKAGES?:\s*(.+?)(?=\.\s*(?:Contact|Info|10%|15%|$))/i);
  if (!match) return [];

  const raw = match[1].split(/\s*·\s*/);
  return raw.map((pkg, i) => {
    const parts = pkg.split(/\s*—\s*/);
    const namePart = parts[0].trim();
    const detail = parts.slice(1).join(" — ").trim();

    const isFree = /free/i.test(namePart) || /free/i.test(detail);
    const fromMatch = namePart.match(/from \$(\d+)/i) ?? detail.match(/from \$(\d+)/i);
    const priceMatch = !fromMatch ? (namePart.match(/\$(\d+)/i) ?? detail.match(/\$(\d+)/i)) : null;

    const price = isFree ? 0 : (fromMatch ? Number(fromMatch[1]) : priceMatch ? Number(priceMatch[1]) : null);
    const label = isFree
      ? "FREE"
      : fromMatch
        ? `FROM $${fromMatch[1]}`
        : priceMatch
          ? `$${priceMatch[1]}`
          : "CONTACT";

    // Heuristic tier assignment by position and keywords
    let tier = Math.min(i, 3);
    if (/sponsor|vendor|regional/i.test(namePart)) tier = 2;
    if (/artist|performance|live set/i.test(namePart)) tier = 3;
    if (/vip/i.test(namePart)) tier = 1;
    if (/general|ga|guest.*general|free/i.test(namePart)) tier = 0;

    return {
      name: namePart.replace(/from \$\d+|\$\d+/gi, "").trim(),
      detail,
      price,
      isFree,
      isFromPrice: !!fromMatch,
      label,
      tier,
    };
  });
}

/* ── Strip ticket block from description for display ── */
function cleanDescription(description: string): string {
  return description
    .replace(/TICKET PACKAGES?:.*?(?=\d{3}-\d{3}-\d{4}|info@|www\.|Contact ISM|$)/is, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/* ── API hook ── */
function useGetEvent(id: number) {
  return useQuery<ISMEvent>({
    queryKey: ["/api/events", id],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/events/${id}`);
      if (!res.ok) throw new Error(res.status === 404 ? "Event not found" : "Failed to load event");
      return res.json();
    },
    enabled: !isNaN(id) && id > 0,
  });
}

/* ── Ticket Package Card ── */
function PackageCard({
  pkg,
  event,
  onSelect,
  isLoading,
}: {
  pkg: TicketPackage;
  event: ISMEvent;
  onSelect: (pkg: TicketPackage) => void;
  isLoading: boolean;
}) {
  const ts = TIER_STYLES[pkg.tier] ?? TIER_STYLES[3];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative rounded-xl border p-5 flex flex-col gap-3 transition-all hover:scale-[1.01]"
      style={{ background: ts.bg, borderColor: ts.border }}
    >
      {/* Tier badge */}
      <div className="flex items-center justify-between">
        <span
          className="text-[9px] font-mono font-bold tracking-[.18em] px-2 py-1 rounded-full border"
          style={{ color: ts.badge, borderColor: `${ts.badge}40`, background: `${ts.badge}18` }}
        >
          {ts.label}
        </span>
        <span
          className="text-sm font-bold font-mono"
          style={{ color: ts.badge }}
        >
          {pkg.label}
        </span>
      </div>

      {/* Package name */}
      <h3 className="font-bold text-lg leading-tight">{pkg.name}</h3>

      {/* Detail */}
      {pkg.detail && (
        <p className="text-xs text-muted-foreground leading-relaxed">{pkg.detail}</p>
      )}

      {/* CTA */}
      <Button
        className="w-full mt-auto font-bold uppercase tracking-widest text-xs gap-2"
        style={pkg.isFree
          ? { background: ts.badge, color: "#000" }
          : { borderColor: ts.border, background: ts.bg }
        }
        variant={pkg.isFree ? "default" : "outline"}
        disabled={isLoading}
        onClick={() => onSelect(pkg)}
      >
        {isLoading ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : pkg.isFree ? (
          <><Ticket className="w-3 h-3" /> RSVP — Free</>
        ) : (
          <><CreditCard className="w-3 h-3" /> {pkg.label} — Get Tickets</>
        )}
      </Button>
    </motion.div>
  );
}

/* ── Main Page ── */
export default function EventDetail() {
  const params = useParams<{ id: string }>();
  const id = parseInt(params.id ?? "", 10);

  const { data: event, isLoading, error } = useGetEvent(id);
  const { toast } = useToast();
  const { isConnected } = useWallet();
  const { startCheckout, isLoading: checkoutLoading } = useStripeCheckout();

  const handleSelectPackage = (pkg: TicketPackage) => {
    if (pkg.isFree) {
      toast({
        title: "🎤 RSVP Confirmed",
        description: `You're on the list for ${event?.title}. See you there!`,
      });
      return;
    }

    const price = pkg.price ?? 0;

    if (isConnected) {
      toast({
        title: "Transaction Initiated",
        description: `Approve the payment in your wallet to secure your ${pkg.name} ticket.`,
      });
    } else {
      startCheckout({
        productName: `${event?.title ?? "Event"} — ${pkg.name}`,
        productType: "event_ticket",
        amountUsd: price,
        metadata: {
          eventId: String(event?.id),
          packageName: pkg.name,
          spaceName: event?.spaceName ?? "",
        },
      });
    }
  };

  /* ── Loading ── */
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-muted-foreground">
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
          <p className="font-mono text-sm tracking-widest uppercase">Loading event…</p>
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (error || !event) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-2xl font-bold">Event not found</p>
          <p className="text-muted-foreground">This event may have been removed or the link is incorrect.</p>
          <Link href="/events">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Events
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const packages = parseTicketPackages(event.description);
  const descClean = cleanDescription(event.description);
  const startDate = new Date(event.startTime);
  const endDate   = new Date(event.endTime);
  const isUrbanFilmFestival = event.id === 19;

  return (
    <div className="min-h-screen bg-background text-foreground pb-24 relative overflow-hidden">

      {/* ── Hero ── */}
      <div className="relative h-72 md:h-96 w-full overflow-hidden">
        <img
          src={event.imageUrl}
          alt={event.title}
          className="w-full h-full object-cover"
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />

        {/* Back link */}
        <div className="absolute top-4 left-4">
          <Link href="/events">
            <Button variant="ghost" size="sm"
              className="gap-1.5 bg-black/50 border border-white/10 backdrop-blur-md hover:bg-black/70 text-white">
              <ArrowLeft className="w-4 h-4" />
              All Events
            </Button>
          </Link>
        </div>

        {/* Live badge */}
        {event.live && (
          <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 animate-pulse">
            <Radio className="w-3 h-3" /> LIVE NOW
          </div>
        )}

        {/* Category pill */}
        <div className="absolute bottom-5 left-5">
          <span className="text-[10px] font-mono tracking-widest uppercase px-3 py-1.5 rounded-full border border-primary/40 bg-primary/20 text-primary">
            {event.category}
          </span>
        </div>
      </div>

      <div className="relative z-10 container mx-auto px-4 -mt-12 max-w-4xl">

        {/* ── Event title + meta ── */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight uppercase leading-tight mb-4">
            {event.title}
          </h1>

          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground font-mono">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-primary shrink-0" />
              {format(startDate, "EEEE, MMMM do, yyyy")}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-primary/60 shrink-0" />
              {new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" }).format(startDate)}
              {" — "}
              {new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York", timeZoneName: "short" }).format(endDate)}
            </span>
            <span className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-primary shrink-0" />
              {event.spaceName}
            </span>
            <span className="flex items-center gap-1.5">
              <Users className="w-4 h-4 text-primary shrink-0" />
              {event.attendeeCount.toLocaleString()}
              {event.maxAttendees ? ` / ${event.maxAttendees.toLocaleString()}` : ""} attending
            </span>
          </div>
        </motion.div>

        {/* ── Host info bar ── */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="flex items-center gap-3 bg-card/40 border border-border rounded-xl px-4 py-3 mb-10 backdrop-blur-sm"
        >
          {event.hostAvatar ? (
            <img src={event.hostAvatar} alt={event.hostName}
              className="w-10 h-10 rounded-full border border-white/20 object-cover shrink-0" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-xs font-bold shrink-0">
              {event.hostName.substring(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Presented by</p>
            <p className="font-bold">{event.hostName}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link href="/world-map">
              <Button variant="outline" size="sm" className="gap-1.5 text-xs border-primary/30 hover:border-primary">
                <MapPin className="w-3 h-3" />
                View on Map
              </Button>
            </Link>
          </div>
        </motion.div>

        {isUrbanFilmFestival && (
          <motion.section
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
            className="mb-10 grid gap-3 sm:grid-cols-2"
          >
            <Link href="/urban-film-festival-miami">
              <Button className="w-full gap-2 font-bold uppercase tracking-widest">
                <Calendar className="w-4 h-4" />
                Explore Festival Schedule
              </Button>
            </Link>
            <a href="https://urbanfilmfestivals.com/registration/" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full gap-2 font-bold uppercase tracking-widest">
                <ExternalLink className="w-4 h-4" />
                Official Registration
              </Button>
            </a>
          </motion.section>
        )}

        {/* ── Ticket Packages ── */}
        {packages.length > 0 && (
          <motion.section
            id="tickets"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <Ticket className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-bold uppercase tracking-widest">Ticket Packages</h2>
            </div>

            {/* Payment method note */}
            <div className="flex flex-wrap gap-2 mb-5">
              <span className="text-[10px] font-mono px-3 py-1.5 rounded-full border border-white/10 bg-white/5 flex items-center gap-1.5">
                <CreditCard className="w-3 h-3 text-primary" />
                Credit / Debit card (Stripe)
              </span>
              <span className="text-[10px] font-mono px-3 py-1.5 rounded-full border border-white/10 bg-white/5 flex items-center gap-1.5">
                <Wallet className="w-3 h-3 text-primary" />
                Crypto wallet · 10–15% discount
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {packages.map((pkg, i) => (
                <PackageCard
                  key={i}
                  pkg={pkg}
                  event={event}
                  onSelect={handleSelectPackage}
                  isLoading={checkoutLoading}
                />
              ))}
            </div>

            <p className="mt-4 text-[10px] text-muted-foreground font-mono">
              Crypto wallet payments receive a 10–15% discount on all paid packages.
              Connect a wallet in the nav to enable.
            </p>
          </motion.section>
        )}

        {/* ── Description ── */}
        {descClean && (
          <motion.section
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="mb-12"
          >
            <h2 className="text-xl font-bold uppercase tracking-widest mb-4">About This Event</h2>
            <div className="bg-card/30 border border-border rounded-xl p-6 text-sm leading-7 text-muted-foreground whitespace-pre-line backdrop-blur-sm">
              {descClean}
            </div>
          </motion.section>
        )}

        {/* ── No packages fallback ── */}
        {packages.length === 0 && (
          <motion.section
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="mb-12 bg-card/30 border border-border rounded-xl p-6"
          >
            <h2 className="font-bold uppercase tracking-widest mb-3 flex items-center gap-2">
              <Ticket className="w-4 h-4 text-primary" /> Tickets & RSVP
            </h2>
            {event.ticketPrice === 0 ? (
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <p className="text-muted-foreground text-sm">This is a free event — no ticket required.</p>
                <Button className="gap-2 bg-green-600 hover:bg-green-500 font-bold"
                  onClick={() => toast({ title: "RSVP Confirmed", description: "You're on the list!" })}>
                  <Ticket className="w-4 h-4" /> RSVP (Free)
                </Button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <p className="text-muted-foreground text-sm">Starting at ${event.ticketPrice}</p>
                <Button className="gap-2 bg-primary hover:bg-primary/80 font-bold" disabled={checkoutLoading}
                  onClick={() => startCheckout({
                    productName: event.title, productType: "event_ticket",
                    amountUsd: event.ticketPrice,
                    metadata: { eventId: String(event.id), spaceName: event.spaceName },
                  })}>
                  {checkoutLoading
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : isConnected
                      ? <><Wallet className="w-4 h-4" /> Get Ticket — {event.currency} {event.ticketPrice}</>
                      : <><CreditCard className="w-4 h-4" /> Get Ticket — ${event.ticketPrice}</>}
                </Button>
              </div>
            )}
          </motion.section>
        )}

        {/* ── Footer actions ── */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="flex flex-wrap gap-3 justify-between items-center border-t border-border pt-8"
        >
          <Link href="/events">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" /> All Events
            </Button>
          </Link>
          <div className="flex gap-2">
            <Link href="/world-map">
              <Button variant="outline" size="sm" className="gap-2">
                <MapPin className="w-3.5 h-3.5" /> Find on Map
              </Button>
            </Link>
            <a href="https://ismnewworld.com" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="gap-2">
                <ExternalLink className="w-3.5 h-3.5" /> ISM New World
              </Button>
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
